import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AbsenceService } from 'src/app/data/absence.service';
import { Filter } from 'src/app/models/common/filter';
import { AbsenceDto } from 'src/app/models/dtos/absence-dto';
import { IPagedResult } from 'src/app/shared/models/PagedResult';
import { AbsenceListRowMapper } from './absence-list-row.mapper';
import { AbsenceListRow } from './absence-list-row.model';

interface AbsenceListRequest {
  page: number;
  count: number;
  sortingField: string;
  sortingDirection: string;
  filters?: Filter[];
}

@Injectable()
export class AbsenceListDataSource extends DataSource<AbsenceListRow> {
  private absenceService = inject(AbsenceService);
  private rowMapper = inject(AbsenceListRowMapper);
  private fetchRequestsSubject = new Subject<AbsenceListRequest>();
  private disconnectSubject = new Subject<void>();

  public absencesSubject = new BehaviorSubject<AbsenceListRow[]>([]);
  private countSubject = new BehaviorSubject<number>(0);
  public count$ = this.countSubject.asObservable();
  private loadingSubject = new BehaviorSubject<boolean>(true);
  public loading$ = this.loadingSubject.asObservable();


  constructor() {
    super();

    this.fetchRequestsSubject
      .pipe(
        tap(() => this.loadingSubject.next(true)),
        switchMap((request) =>
          this.absenceService
            .getAbsences(
              request.page,
              request.count,
              request.sortingField,
              request.sortingDirection,
              request.filters ?? [],
            )
            .pipe(
              catchError(() => of(<IPagedResult<AbsenceDto>>{ Results: [], Count: 0 })),
              finalize(() => this.loadingSubject.next(false)),
            )
        ),
        takeUntil(this.disconnectSubject),
      )
      .subscribe((response: IPagedResult<AbsenceDto>) => {
        this.absencesSubject.next((response.Results ?? []).map(this.rowMapper.map));
        this.countSubject.next(response.Count ?? 0);
      });
  }

  connect(collectionViewer: CollectionViewer): Observable<AbsenceListRow[]> {
    return this.absencesSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.disconnectSubject.next();
    this.disconnectSubject.complete();
    this.fetchRequestsSubject.complete();
    this.absencesSubject.complete();
    this.loadingSubject.complete();
    this.countSubject.complete();
  }

  fetchAbsences(
    page: number,
    count: number,
    sortingField: string,
    sortingDirection: string,
    filters: Filter[]
  ) {
    this.fetchRequestsSubject.next({
      page,
      count,
      sortingField,
      sortingDirection,
      filters,
    });
  }
}